import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "./Icon";
import { cx } from "./ui";
import { errorMessage } from "@/lib/errors";

type Toast = { id: number; kind: "success" | "error"; message: string };
/** `error` takes a ready message (string) or any thrown error, translated by its API code. */
type ToastApi = { success: (message: string) => void; error: (error: unknown) => void };

const ToastContext = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);

  const push = useCallback((kind: Toast["kind"], message: string) => {
    const id = nextId.current++;
    setToasts((ts) => [...ts.slice(-3), { id, kind, message }]);
    window.setTimeout(() => setToasts((ts) => ts.filter((x) => x.id !== id)), kind === "error" ? 6000 : 3500);
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      success: (message) => push("success", message),
      error: (error) => push("error", typeof error === "string" ? error : errorMessage(t, error)),
    }),
    [push, t],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        aria-live="polite"
        className="pointer-events-none fixed right-4 bottom-4 left-4 z-50 flex flex-col items-end gap-2 sm:left-auto"
      >
        {toasts.map((toast) => (
          <div
            key={toast.id}
            role={toast.kind === "error" ? "alert" : "status"}
            className={cx(
              "pointer-events-auto flex max-w-sm items-start gap-2.5 rounded-[var(--radius-card)] border px-3.5 py-2.5 text-sm font-medium shadow-[var(--shadow)]",
              toast.kind === "error"
                ? "border-fail-fg/30 bg-fail-bg text-fail-fg"
                : "border-border bg-surface text-text",
            )}
          >
            <Icon
              name={toast.kind === "error" ? "alert" : "check"}
              className={cx("mt-0.5", toast.kind === "success" && "text-pass-fg")}
            />
            <span>{toast.message}</span>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast outside ToastProvider");
  return ctx;
}
